import { useState } from 'react';
import { auth, db } from '@/lib/firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import { sanitizeInput } from '@/lib/sanitize';

declare const __BASE_PATH__: string;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const sanitizedEmail = sanitizeInput(email);
    const attemptsKey = `login_attempts_${sanitizedEmail}`;
    
    // Verificar límite de intentos
    const attemptsData = JSON.parse(localStorage.getItem(attemptsKey) || '{"count": 0, "lastAttempt": 0}');
    const now = Date.now();
    
    if (attemptsData.count >= 5 && now - attemptsData.lastAttempt < 30 * 60 * 1000) {
      const minutesLeft = Math.ceil((30 * 60 * 1000 - (now - attemptsData.lastAttempt)) / 60000);
      setError(`Demasiados intentos fallidos. Por favor, espera ${minutesLeft} minutos.`);
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, sanitizedEmail, password);
      localStorage.removeItem(attemptsKey); // Limpiar intentos en caso de éxito
      
      // Buscar el rol directamente para redirigir
      const user = userCredential.user;
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      
      let userRole = 'client';
      if (docSnap.exists()) {
        userRole = docSnap.data().role || 'client';
      }
      
      // Forzar admin temporalmente para pruebas según lo acordado
      userRole = 'admin';
      
      if (userRole === 'admin') {
        navigate('/crm/admin');
      } else if (userRole === 'employee') {
        navigate('/crm/employee');
      } else {
        navigate('/crm/client');
      }
    } catch (err: any) {
      // Actualizar intentos en caso de fallo
      const currentAttempts = JSON.parse(localStorage.getItem(attemptsKey) || '{"count": 0, "lastAttempt": 0}');
      let newCount = currentAttempts.count;
      
      if (now - currentAttempts.lastAttempt > 30 * 60 * 1000) {
        newCount = 1;
      } else {
        newCount += 1;
      }
      
      localStorage.setItem(attemptsKey, JSON.stringify({ count: newCount, lastAttempt: now }));
      
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-indigo-950 text-white font-sans">
      <div className="max-w-md w-full p-8 bg-slate-800/50 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-700/50">
        <Link to="/" className="inline-flex items-center text-sm text-slate-400 hover:text-emerald-400 transition-colors mb-6">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Volver al inicio
        </Link>
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
            Smart CRM
          </h1>
          <p className="text-slate-400 mt-2">Bienvenido de nuevo</p>
        </div>
        
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-500 p-3 rounded-lg mb-6 text-sm text-center">
            {error}
          </div>
        )}
        
        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2 text-slate-300">Correo Electrónico</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 rounded-lg bg-slate-700/50 border border-slate-600 focus:border-emerald-500 focus:outline-none transition-colors text-white"
              placeholder="tu@email.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-slate-300">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 rounded-lg bg-slate-700/50 border border-slate-600 focus:border-emerald-500 focus:outline-none transition-colors text-white"
              placeholder="••••••••"
              required
            />
          </div>
          
          <button
            type="submit"
            className="w-full p-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 rounded-lg font-semibold transition-all duration-300 transform hover:scale-[1.02] shadow-lg shadow-emerald-500/25"
          >
            Iniciar Sesión
          </button>
        </form>
      </div>
    </div>
  );
}
